import { MeetingStatus, ProposedTimeSlot, User, UserType } from "../../../types";
import { userHasPermission } from "../../../types-helpers";

export interface MeetingFormValues {
  meetingType: MeetingStatus.Scheduled | MeetingStatus.Proposed;
  startTime: Date;
  organisationIds: string[];
  patientId: string;
  attendeeIds: string[];
  notifyTimeAttendees?: string[];
  inviteEmails: string[];
  proposedTimes?: ProposedTimeSlot[];
}

export interface MeetingFormValidationContext {
  userId: string;
  users?: User[];
}

// Basic email validation for invitees and contact addresses.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (value: string): boolean => {
  return EMAIL_PATTERN.test(value.trim());
};

// Validate the form as the user edits or submits a meeting.
export const validateMeetingForm = (
  values: MeetingFormValues,
  validationContext: MeetingFormValidationContext,
): Partial<Record<keyof MeetingFormValues, string>> => {
  const errors: Partial<Record<keyof MeetingFormValues, string>> = {};

  if (!values.patientId) {
    errors.patientId = "A patient is required";
  }

  if (values.meetingType === MeetingStatus.Scheduled) {
    const startTimeError = checkScheduledStartTime(values.startTime);
    if (startTimeError) {
      errors.startTime = startTimeError;
    }
  }

  if (values.meetingType === MeetingStatus.Proposed) {
    const proposedTimesError = checkProposedTimes(values.proposedTimes);
    if (proposedTimesError) {
      errors.proposedTimes = proposedTimesError;
    }
  }

  const attendeeError = checkForUserError(validationContext, values.meetingType, values.attendeeIds);
  if (attendeeError) {
    errors.attendeeIds = attendeeError;
  }

  if (!values.inviteEmails.every(isValidEmail)) {
    errors.inviteEmails = "One or more invite emails are invalid";
  }

  return errors;
};

// Scheduled meetings must have a valid start time that is in the future.
const checkScheduledStartTime = (startTime: Date | undefined): string | undefined => {
  if (!startTime || Number.isNaN(startTime.getTime())) {
    return "A start time is required";
  }

  if (startTime < new Date()) {
    return "Start time must be in the future";
  }

  return undefined;
};

// Determine whether the attendee list is valid for the meeting type.
const checkForUserError = (
  validationContext: MeetingFormValidationContext,
  type: MeetingStatus,
  attendeeIds: string[],
): string | undefined => {
  const { userId, users } = validationContext;

  // Proposed meetings must include the current user to avoid ghost invitations.
  if (type === MeetingStatus.Proposed && !attendeeIds.includes(userId)) {
    return "You must be part of a proposed meeting";
  }

  const verifiedAttendeeCount = users
    ? users.filter((user) => userHasPermission(UserType.User, user) && attendeeIds.includes(user.userId)).length
    : attendeeIds.length;

  if (type === MeetingStatus.Proposed && verifiedAttendeeCount < 2) {
    return "You must include at least two verified users as attendees for a proposed meeting";
  }

  if (verifiedAttendeeCount === 0) {
    return "You must include at least one verified user as a meeting attendee";
  }

  return undefined;
};

// Validate the candidate time slots for a proposed meeting.
const checkProposedTimes = (proposedTimes: ProposedTimeSlot[] | undefined): string | undefined => {
  if (!proposedTimes || proposedTimes.length === 0) {
    return "Missing proposed times";
  }

  const now = new Date();
  for (const slot of proposedTimes) {
    if (slot.startTime < now) {
      return "Proposed time was in the past";
    }

    if (slot.endTime <= slot.startTime) {
      return "Invalid proposed start and end time";
    }
  }

  return undefined;
};

// --- Modal-level error derivation ---
// Formik's `validate` errors (above) are the source of truth for what's wrong with the form.
// This section only adds the step-gated banner/fallback text the schedule/edit modals need on
// top of those errors, and is the single place both modals pull from.

const PATIENT_REQUIRED_MODAL_ERROR = "A patient is required to create a meeting";
const START_TIME_REQUIRED_MODAL_ERROR = "At least one timeslot must be selected";

export const MODAL_ERROR_SX = {
  border: "1px solid #fecaca",
  borderRadius: 2,
  backgroundColor: "#fef2f2",
  px: 1.5,
  py: 1,
  color: "#b91c1c",
} as const;

// Formik reports errors as a string, a string array (list fields), or a nested object; only the first two are renderable.
export const asTextOrList = (value: unknown): string | string[] | undefined => {
  if (typeof value === "string" || Array.isArray(value)) {
    return value;
  }

  return undefined;
};

const getAttendeeFieldError = (attendeeIdsFormikError: unknown, inviteEmailsFormikError: unknown): string | string[] | undefined => {
  return asTextOrList(attendeeIdsFormikError) || asTextOrList(inviteEmailsFormikError);
};

export interface MeetingModalErrors {
  modalErrors: string[];
  attendeeFieldError: string | string[] | undefined;
  canSubmit: boolean;
}

export interface ScheduleMeetingModalErrorContext {
  activeStep: number;
  hasClickedFinalisationWithoutTime: boolean;
  hasPatientSelected: boolean;
  hasAnySelectedTime: boolean;
  attendeeIds: string[];
  inviteEmails: string[];
  formikErrors: {
    patientId?: unknown;
    startTime?: unknown;
    attendeeIds?: unknown;
    inviteEmails?: unknown;
  };
}

// Combines formik field errors with step/UI state into the banner errors shown in the schedule modal.
export const getScheduleMeetingModalErrors = (context: ScheduleMeetingModalErrorContext): MeetingModalErrors => {
  const {
    activeStep,
    hasClickedFinalisationWithoutTime,
    hasPatientSelected,
    hasAnySelectedTime,
    attendeeIds,
    inviteEmails,
    formikErrors,
  } = context;

  const rawStartTimeError = asTextOrList(formikErrors.startTime);
  // Before a time is picked, only show the "required" error once the user has tried to move on.
  const startTimeRequiredModalError =
    hasClickedFinalisationWithoutTime && !hasAnySelectedTime
      ? (rawStartTimeError || START_TIME_REQUIRED_MODAL_ERROR)
      : undefined;
  // Once a time is picked, surface formik's own validation message (e.g. "in the past") immediately.
  const startTimeConstraintErrors = hasAnySelectedTime && rawStartTimeError
    ? (Array.isArray(rawStartTimeError) ? rawStartTimeError : [rawStartTimeError])
    : [];

  const patientFieldError = asTextOrList(formikErrors.patientId);
  const patientModalError =
    activeStep > 1 && (!hasPatientSelected || !!patientFieldError)
      ? (patientFieldError || PATIENT_REQUIRED_MODAL_ERROR)
      : undefined;

  const modalErrors = [
    patientModalError,
    startTimeRequiredModalError,
    ...startTimeConstraintErrors,
  ].filter((error): error is string => typeof error === "string");

  const hasAttendeeSelected = attendeeIds.length > 0 || inviteEmails.length > 0;
  const canSubmit = hasPatientSelected && hasAnySelectedTime && hasAttendeeSelected;

  return {
    modalErrors,
    attendeeFieldError: getAttendeeFieldError(formikErrors.attendeeIds, formikErrors.inviteEmails),
    canSubmit,
  };
};

export interface EditMeetingModalErrorContext {
  attendeeIds: string[];
  inviteEmails: string[];
  formikErrors: {
    attendeeIds?: unknown;
    inviteEmails?: unknown;
  };
}

// Combines formik field errors with UI state into the banner errors shown in the edit modal.
export const getEditMeetingModalErrors = (context: EditMeetingModalErrorContext): MeetingModalErrors => {
  const { attendeeIds, inviteEmails, formikErrors } = context;

  const hasAttendeeSelected = attendeeIds.length > 0 || inviteEmails.length > 0;

  return {
    modalErrors: [],
    attendeeFieldError: getAttendeeFieldError(formikErrors.attendeeIds, formikErrors.inviteEmails),
    canSubmit: hasAttendeeSelected,
  };
};